import { useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import type { Dictionary } from '../i18n';
import type { LoadedImage } from '../state/useImage';

interface Props {
  t: Dictionary;
  image: LoadedImage | null;
  isLoading: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
}

export function ImageDropZone({ t, image, isLoading, onFile, onClear }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOver, setIsOver] = useState(false);

  const pick = () => inputRef.current?.click();

  return (
    <Box>
      <Box
        onDragOver={(event) => {
          event.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsOver(false);
          const file = event.dataTransfer.files[0];
          if (file) onFile(file);
        }}
        onClick={pick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            pick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={t.uploadTitle}
        sx={{
          border: '2px dashed',
          borderColor: isOver ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isOver ? 'action.hover' : 'transparent',
          transition: 'border-color 120ms, background-color 120ms',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {image ? (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box
              component="img"
              src={image.objectUrl}
              alt=""
              sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
              <Typography variant="body2" noWrap>
                {image.width} × {image.height}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t.replaceImage}
              </Typography>
            </Box>
            <Button
              size="small"
              color="inherit"
              startIcon={<DeleteOutlinedIcon />}
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
            >
              {t.removeImage}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ py: 1, alignItems: 'center' }}>
            <AddPhotoAlternateOutlinedIcon color="primary" fontSize="large" />
            <Typography variant="subtitle2">{isLoading ? '…' : t.uploadTitle}</Typography>
            <Typography variant="caption" color="text.secondary">
              {isOver ? t.uploadDrop : t.uploadHint}
            </Typography>
          </Stack>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          // Reset so picking the same file twice still fires a change event.
          event.target.value = '';
        }}
      />
    </Box>
  );
}
